import { type FC, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form-latest';
import CageCardsTemplate4x2 from '@/components/Modals/PrintCageCards/4x2/CageCardsTemplate';
import CageCardTemplate4x2 from '@/components/Modals/PrintCageCards/4x2/CageCardTemplate';
import CageCardsTemplate4x3 from '@/components/Modals/PrintCageCards/4x3/CageCardsTemplate';
import CageCardTemplate4x3 from '@/components/Modals/PrintCageCards/4x3/CageCardTemplate';
import Header from '@/components/UI/Header';
import Link from '@/components/UI/Link';
import SearchSelect from '@/components/UI/SearchSelect';
import type { ID } from '@/model/Common.model';
import { CageCardSize, type CageCardSettings } from '@/model/Team.model';
import { cageCardOptions } from '@/referenceData/global-data/team/cageCardSettings';
import { RouteService } from '@/support/RouteService';

export interface MetadataOption {
  id: ID;
  label: string;
  value: string;
}

export interface CageCardsFormData {
  size: CageCardSize;
  selectedMetadata: Array<ID>;
}

export interface CageCardsProps {
  studyMetadata: Array<MetadataOption>;
  cageMetadata: Array<MetadataOption>;
  onSubmit: (data: CageCardsFormData) => void | Promise<void>;
  value?: CageCardSettings;
}

const maxFieldsMap: Record<CageCardSize, number> = {
  [CageCardSize.FourByThree]: 8,
  [CageCardSize.FourByTwo]: 8,
};

export const cageCardTemplateMap = {
  [CageCardSize.FourByThree]: CageCardTemplate4x3,
  [CageCardSize.FourByTwo]: CageCardTemplate4x2,
} as const;

export const cageCardsTemplateMap = {
  [CageCardSize.FourByThree]: CageCardsTemplate4x3,
  [CageCardSize.FourByTwo]: CageCardsTemplate4x2,
} as const;

const CageCards: FC<CageCardsProps> = ({ studyMetadata, cageMetadata, onSubmit, value }) => {
  const formMethods = useForm<CageCardsFormData>({
    defaultValues: {
      size: value?.size ?? cageCardOptions[0].value,
      selectedMetadata: (value?.metadata as Array<ID>) ?? [],
    },
  });

  const { register, control, handleSubmit, watch } = formMethods;

  const sizeValue = watch('size');
  const selectedMetadataValue = watch('selectedMetadata');

  const sections = useMemo(
    () => [
      { title: 'Study', items: studyMetadata },
      { title: 'Cage', items: cageMetadata },
    ],
    [studyMetadata, cageMetadata]
  );

  const exampleCageCardData = useMemo(() => {
    const allMetadata: Array<MetadataOption> = [...studyMetadata, ...cageMetadata];
    const findMetadataItem = (id: ID): MetadataOption | undefined => allMetadata.find((option) => option.id === id);

    return {
      cage: {
        id: 1,
        name: 'Cage 1',
        catalog: 'C00001',
        subjects: {
          data: [
            { id: 1, name: 'Animal 1', sex: 'm', dob: '2021-01-01', strain: 'C57CL/6' },
            { id: 2, name: 'Animal 2', sex: 'f', dob: '2021-01-01', strain: 'C57CL/6' },
            { id: 3, name: 'Animal 3', sex: 'm', dob: '2021-01-01', strain: 'C57CL/6' },
          ],
        },
      },
      study: {
        name: 'Study 1',
        users: { data: [{ id: 1, name: 'Connie Watsica' }] },
      },
      selectedMetadata: selectedMetadataValue.map((id) => {
        const option = findMetadataItem(id);
        return {
          id,
          title: <i>{option?.label ?? ''}</i>,
          value: '',
        };
      }),
    };
  }, [selectedMetadataValue, studyMetadata, cageMetadata]);

  const { cage, study, selectedMetadata } = exampleCageCardData;
  const maxFields = maxFieldsMap[sizeValue];
  const CageCardTemplate = cageCardTemplateMap[sizeValue];

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)} data-test-component="CageCards" data-test-element="form">
        <div>
          <div className="mb3">
            <Header mainHeaderText="Cage cards" />
            <h3 className="f6 normal lh-title pb2 dark-gray mb3">
              Choose how cage cards are printed and which fields of metadata are included.
              <Link className="link blue" to={RouteService.help('cage_cards')} openTab>
                Read more
              </Link>
            </h3>
          </div>
          <div className="flex flex-row">
            <div className="mr4" style={{ width: 500 }}>
              <div className="ui-card pa3 mb4">
                <div className="mb4" style={{ display: 'none' }}>
                  <label htmlFor="size" className="mb3">
                    What size of labels will you be printing?
                  </label>
                  {cageCardOptions.map((cardOption) => (
                    <div className="flex items-center pb2" key={cardOption.value}>
                      <input
                        id={cardOption.value}
                        type="radio"
                        defaultChecked={sizeValue === cardOption.value}
                        value={cardOption.value}
                        className="pointer ui__form__radio"
                        style={{ marginBottom: 0 }}
                        {...register('size')}
                      />
                      <label className="pointer pl3" style={{ marginBottom: 0 }} htmlFor={cardOption.value}>
                        {cardOption.label}
                      </label>
                    </div>
                  ))}
                </div>

                <div className="mb4">
                  <label htmlFor="selectedMetadata" className="mb3">
                    Which metadata would you like to include? Choose up to {maxFields} fields
                  </label>
                  <div className="ui-card">
                    <Controller
                      name="selectedMetadata"
                      control={control}
                      render={({ field: { value: selected, onChange } }) => (
                        <SearchSelect
                          sections={sections}
                          selected={selected}
                          setSelected={onChange}
                          sort
                          sortAccessor="label"
                          maxSelections={maxFields}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
              <button type="submit" className="mr2" data-testid="cage-cards-submit">
                Save
              </button>
            </div>
            <div className="ui-card pa3 self-start">
              <CageCardTemplate
                cage={cage}
                study={study}
                selectedMetadata={selectedMetadata}
                maxFields={maxFields}
                species={[]}
              />
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default CageCards;
